import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarDays,
  CheckCircle2,
  ContactRound,
  CornerDownRight,
  Download,
  Eye,
  FileText,
  Image,
  Loader2,
  Mail,
  Paperclip,
  XCircle,
} from 'lucide-react';

import {
  getWorkflowRunAttachment,
  type WorkflowRunAttachment,
  type WorkflowRunAttachmentAsset,
  type WorkflowRunAttachmentStatus,
  type WorkflowRunAttachmentSummary,
} from '$api/WorkflowRunsAPI';
import { Stack } from '$layouts/v2/components/Stack';

const formatBytes = (size: number): string => {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const processingStatusMeta = {
  not_required: { label: 'No attachments', color: 'default' as const },
  pending: { label: 'Processing', color: 'warning' as const },
  ready: { label: 'Ready', color: 'success' as const },
};

const attachmentStatusMeta: Record<
  WorkflowRunAttachmentStatus,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' }
> = {
  pending: { label: 'Pending', color: 'warning' },
  processing: { label: 'Processing', color: 'warning' },
  processed: { label: 'Processed', color: 'success' },
  unsupported: { label: 'Unsupported', color: 'default' },
  failed: { label: 'Failed', color: 'error' },
};

const itemTypeMeta = {
  message: { label: 'Attached email', icon: Mail },
  event: { label: 'Attached event', icon: CalendarDays },
  contact: { label: 'Attached contact', icon: ContactRound },
};

const AttachmentStatusIcon = ({
  status,
}: {
  status: WorkflowRunAttachmentStatus;
}) => {
  if (status === 'processed') {
    return <CheckCircle2 size={14} />;
  }
  if (status === 'failed') {
    return <XCircle size={14} />;
  }
  if (status === 'pending' || status === 'processing') {
    return <Loader2 size={14} />;
  }
  return <FileText size={14} />;
};

function AttachmentRow({
  attachment,
  parentName,
  token,
  workflowRunId,
}: {
  attachment: WorkflowRunAttachment;
  parentName?: string;
  token?: string | null;
  workflowRunId?: string;
}) {
  const status = attachmentStatusMeta[attachment.status];
  const itemType = attachment.itemType
    ? itemTypeMeta[attachment.itemType]
    : undefined;
  const ContentIcon = attachment.isInline ? Image : itemType?.icon ?? FileText;
  const canInteract = Boolean(token && workflowRunId);
  const [viewerAsset, setViewerAsset] =
    useState<WorkflowRunAttachmentAsset | null>(null);
  const [content, setContent] = useState<Record<string, string>>({});
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!viewerAsset || !token || !workflowRunId || content[viewerAsset]) {
      return;
    }

    let cancelled = false;
    setIsLoadingContent(true);
    setContentError(null);
    getWorkflowRunAttachment(
      token,
      workflowRunId,
      attachment.attachmentId,
      viewerAsset,
    )
      .then((response) => response.text())
      .then((value) => {
        if (!cancelled) {
          setContent((previous) => ({ ...previous, [viewerAsset]: value }));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setContentError(String(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingContent(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attachment.attachmentId, content, token, viewerAsset, workflowRunId]);

  const downloadOriginal = async () => {
    if (!token || !workflowRunId) {
      return;
    }
    setIsDownloading(true);
    try {
      const response = await getWorkflowRunAttachment(
        token,
        workflowRunId,
        attachment.attachmentId,
        'original',
      );
      const blobUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = attachment.name;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setContentError(String(error));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack
      spacing={0.6}
      sx={{ py: 1, pl: attachment.nestingDepth > 0 ? 2.5 : 0 }}
    >
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        {attachment.nestingDepth > 0 && <CornerDownRight size={14} />}
        <ContentIcon size={15} />
        <Tooltip title={attachment.name}>
          <Typography
            variant="body2"
            noWrap
            sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
          >
            {attachment.name}
          </Typography>
        </Tooltip>
        <Chip
          size="small"
          color={status.color}
          variant={attachment.status === 'processed' ? 'filled' : 'outlined'}
          icon={<AttachmentStatusIcon status={attachment.status} />}
          label={status.label}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ pl: 2.4, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {attachment.contentType} · {formatBytes(attachment.size)}
        </Typography>
        {attachment.isInline && (
          <Chip size="small" variant="outlined" label="Inline image" />
        )}
        {itemType && (
          <Chip size="small" variant="outlined" label={itemType.label} />
        )}
        {parentName && (
          <Chip
            size="small"
            variant="outlined"
            label={`Inside ${parentName}`}
          />
        )}
        {attachment.markdownAvailable && (
          <Chip size="small" variant="outlined" color="info" label="Markdown" />
        )}
        {attachment.textAvailable && (
          <Chip size="small" variant="outlined" color="info" label="Text" />
        )}
      </Stack>

      {canInteract &&
        (attachment.kind === 'file' || attachment.kind === 'item') &&
        (attachment.originalAvailable ||
          attachment.markdownAvailable ||
          attachment.textAvailable) && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ pl: 2.1, flexWrap: 'wrap', rowGap: 0.5 }}
          >
            {attachment.originalAvailable && (
              <Button
                size="small"
                variant="text"
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={13} />
                  ) : (
                    <Download size={14} />
                  )
                }
                disabled={isDownloading}
                onClick={() => void downloadOriginal()}
                sx={{ minWidth: 0, px: 0.75 }}
              >
                Download
              </Button>
            )}
            {attachment.markdownAvailable && (
              <Button
                size="small"
                variant="text"
                startIcon={<Eye size={14} />}
                onClick={() => setViewerAsset('markdown')}
                sx={{ minWidth: 0, px: 0.75 }}
              >
                View markdown
              </Button>
            )}
            {attachment.textAvailable && (
              <Button
                size="small"
                variant="text"
                startIcon={<Eye size={14} />}
                onClick={() => setViewerAsset('text')}
                sx={{ minWidth: 0, px: 0.75 }}
              >
                View text
              </Button>
            )}
          </Stack>
        )}

      {attachment.error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ pl: 2.4, overflowWrap: 'anywhere' }}
        >
          {attachment.error}
        </Typography>
      )}

      {attachment.processingWarnings?.map((warning) => (
        <Typography
          key={`${warning.code}:${warning.message}`}
          variant="caption"
          sx={{
            color: 'warning.main',
            pl: 2.4,
            overflowWrap: 'anywhere',
          }}
        >
          {warning.message}
        </Typography>
      ))}

      {contentError && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          {contentError}
        </Alert>
      )}

      <Dialog
        open={viewerAsset !== null}
        onClose={() => setViewerAsset(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {attachment.name} · {viewerAsset}
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingContent && <CircularProgress size={20} />}
          {!isLoadingContent && viewerAsset && (
            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.55,
              }}
            >
              {content[viewerAsset] || 'No content was returned.'}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

export function WorkflowRunAttachments({
  summary,
  compact = false,
  token,
  workflowRunId,
}: {
  summary: WorkflowRunAttachmentSummary;
  compact?: boolean;
  token?: string | null;
  workflowRunId?: string;
}) {
  const status = processingStatusMeta[summary.processingStatus];

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.25 : 1.5 }}>
      <Stack spacing={compact ? 0.75 : 1}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Paperclip size={15} />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Attachments
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={status.label}
            color={status.color}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {summary.total}
          </Typography>
        </Stack>

        {summary.total === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            No attachments were recorded for this email.
          </Typography>
        ) : (
          <>
            {!compact && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {summary.processed} processed · {summary.unsupported}{' '}
                unsupported
                {summary.failed ? ` · ${summary.failed} failed` : ''}
                {summary.pending || summary.processing
                  ? ` · ${
                      summary.pending + summary.processing
                    } still processing`
                  : ''}
              </Typography>
            )}
            <Box>
              {summary.items.map((attachment, index) => (
                <Box key={attachment.attachmentId}>
                  {index > 0 && <Divider />}
                  <AttachmentRow
                    attachment={attachment}
                    parentName={
                      attachment.parentAttachmentId
                        ? summary.items.find(
                            (item) =>
                              item.attachmentId ===
                              attachment.parentAttachmentId,
                          )?.name
                        : undefined
                    }
                    token={token}
                    workflowRunId={workflowRunId}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
}
